import { FocusableOption, FocusKeyManager } from '@angular/cdk/a11y';
import { ENTER, ESCAPE } from '@angular/cdk/keycodes';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AutoFocusDirective, InputComponent } from '@woolworthsnz/form';
import { SHOPPING_LISTS_PATH } from '@woolworthsnz/shop';
import {
	AppSettingsService,
	ButtonGroupComponent,
	SvgIconComponent,
	EmbeddedVisibilityDirective,
	ButtonComponent,
	CustomWindow,
	LoggingService,
	WINDOW,
} from '@woolworthsnz/styleguide';
import equal from 'fast-deep-equal';
import { of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { ShoppingListService, ShoppingListState } from '../../services/shopping-list.service';
import {
	AfterContentInit,
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Inject,
	NgZone,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { NgIf, NgClass, NgFor } from '@angular/common';

@Component({
	selector: 'wnz-shopping-list',
	templateUrl: './shopping-list.component.html',
	styleUrls: ['./shopping-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		EmbeddedVisibilityDirective,
		SvgIconComponent,
		ButtonComponent,
		RouterLink,
		NgClass,
		NgFor,
		AutoFocusDirective,
		ButtonGroupComponent,
		FormsModule,
		ReactiveFormsModule,
		InputComponent,
	],
})
export class ShoppingListComponent implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {
	@ViewChild('shoppingListElement') shoppingListElem: ElementRef;
	@ViewChild('listItems') shoppingListItemsElem: ElementRef;
	@ViewChild('editInput') editInput: InputComponent;
	@ViewChildren('shoppingListItem') shoppingListItemButtons: QueryList<ButtonComponent>;

	currentScrollLeft = 0;
	indexOfItemBeingEdited: number | undefined;
	isOpen = false;
	isInEditMode = false;
	noItemsButtonText = 'Add items';
	searchbarElem: HTMLInputElement;
	shoppingListPage = `/${SHOPPING_LISTS_PATH}`;
	selectedItemIndex: number | undefined = 0;
	shoppingListItems: string[];
	shoppingListEditForm: FormGroup;
	showPrevBtn = false;
	showNextBtn = false;
	keyManager: FocusKeyManager<ButtonComponent>;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		@Inject(WINDOW) private window: CustomWindow,
		private cdr: ChangeDetectorRef,
		private fb: FormBuilder,
		private router: Router,
		private route: ActivatedRoute,
		private shoppingListService: ShoppingListService,
		private loggingService: LoggingService,
		public ngZone: NgZone,
		private appSettingsService: AppSettingsService
	) {
		this.shoppingListEditForm = this.fb.group({
			itemName: '',
		});
	}

	get searchQuery(): string {
		return this.route.snapshot.queryParamMap.get('search') || '';
	}

	@HostListener('keydown', ['$event'])
	onKeydown(event: any): void {
		this.keyManager.onKeydown(event);
	}

	@HostListener('window:resize', ['$event'])
	onResize(): void {
		this.toggleNextAndPrevButtons();
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	ngOnInit(): void {
		this.searchbarElem = <HTMLInputElement>document.getElementById('search');
		this.shoppingListService.state$
			.pipe(
				distinctUntilChanged((a, b) => equal(a, b)),
				takeUntil(this.destroyed$)
			)
			.subscribe((r: ShoppingListState) => {
				this.isOpen = Boolean(r.isOpen);

				this.shoppingListItems = r.items || [];
				this.selectedItemIndex = r.selectedItemIndex;

				this.toggleNextAndPrevButtons();
				// let it render the new children before trying to focus
				setTimeout(() => this.focusOnShoppingListItem(this.selectedItemIndex || 0));

				this.cdr.markForCheck();
			});

		this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe((queryParams) => {
			const searchQueryIndex = this.shoppingListItems.indexOf(this.searchQuery);

			this.shoppingListService.setState({
				isOpen: !!queryParams.openShoppingList,
				selectedItemIndex: searchQueryIndex > -1 ? searchQueryIndex : this.selectedItemIndex,
			});
		});
	}

	ngAfterViewInit(): void {
		if (this.shoppingListElem && this.shoppingListElem.nativeElement) {
			this.shoppingListElem.nativeElement.addEventListener('scroll', this.handleScroll);
		}
	}

	ngAfterContentInit(): void {
		this.keyManager = new FocusKeyManager(
			<QueryList<FocusableOption & ButtonComponent>>this.shoppingListItemButtons
		).withWrap();
	}

	backToItemList(): void {
		this.focusOnShoppingListItem(this.indexOfItemBeingEdited || 0);
		this.toggleEditMode(false);
		this.indexOfItemBeingEdited = undefined;

		this.cdr.markForCheck();
	}

	deleteItem(): void {
		this.shoppingListService
			.removeItemFromShoppingList(this.indexOfItemBeingEdited || 0)
			.pipe(
				take(1),
				catchError((e) => {
					this.focusOnShoppingListItem(this.indexOfItemBeingEdited || 0);
					this.loggingService.trackException({ error: e });

					return of(e);
				})
			)
			.subscribe(() => {
				const indexOfNextItemInList = this.getIndexOfNextItemInList();
				this.focusOnShoppingListItem(indexOfNextItemInList || 0);
				this.toggleEditMode(false);
				this.handleShoppingListItemSelection(indexOfNextItemInList || 0);
				this.toggleNextAndPrevButtons();
				this.indexOfItemBeingEdited = undefined;

				this.cdr.markForCheck();
			});
	}

	getIndexOfNextItemInList = (): number | undefined =>
		this.shoppingListItems.length <= (this.indexOfItemBeingEdited || 0) ? 0 : this.indexOfItemBeingEdited;

	canEdit = (index: number): boolean => index === this.selectedItemIndex;

	editShoppingListItem(index: number): void {
		if (this.editInput && this.editInput.input) {
			this.editInput.input.nativeElement.focus();
		}

		this.toggleEditMode(true);

		this.currentScrollLeft = this.shoppingListElem.nativeElement.scrollLeft;
		this.indexOfItemBeingEdited = index;
		this.shoppingListEditForm.patchValue({ itemName: this.shoppingListItems[index] });
	}

	handleScroll = (): void => {
		this.ngZone.runOutsideAngular(() => {
			requestAnimationFrame(() => {
				this.currentScrollLeft = this.shoppingListElem.nativeElement.scrollLeft;
				this.toggleNextAndPrevButtons();
			});
		});
	};

	prevClick(): void {
		this.shoppingListElem.nativeElement.scrollLeft = this.currentScrollLeft =
			this.currentScrollLeft - this.window.innerWidth / 2;
	}

	nextClick(): void {
		this.shoppingListElem.nativeElement.scrollLeft = this.currentScrollLeft =
			this.currentScrollLeft + this.window.innerWidth / 2;
	}

	toggleEditMode(inEditMode: boolean): void {
		this.isInEditMode = inEditMode;
	}

	toggleNextAndPrevButtons(): void {
		const scrollWidth = (this.shoppingListItemsElem && this.shoppingListItemsElem.nativeElement.scrollWidth) || 0;
		const shoppingListIsBiggerThanScreen = scrollWidth > this.window.innerWidth;
		const scrollLeftIsZero = this.currentScrollLeft === 0;
		const scrollLeftIsMax = this.currentScrollLeft + this.window.innerWidth >= scrollWidth;

		this.showNextBtn = this.isOpen && shoppingListIsBiggerThanScreen && !scrollLeftIsMax;
		this.showPrevBtn = this.isOpen && !scrollLeftIsZero;

		this.cdr.markForCheck();
	}

	focusOnShoppingListItem = (index: number): void => {
		if (!this.shoppingListItemButtons) {
			return;
		}

		const button: ButtonComponent = this.shoppingListItemButtons.toArray()[index];

		if (!button) {
			return;
		}

		this.updateMainSearchBar(this.shoppingListItems[index]);

		setTimeout(() => {
			button.el.nativeElement.focus();
		}, 1);
	};

	getButtonFillStyle = (index: number): string =>
		index === this.selectedItemIndex ? 'secondary' : 'secondaryOutline';

	handleShoppingListItemClick(index: number): void {
		if (this.canEdit(index)) {
			return this.editShoppingListItem(index);
		}

		return this.handleShoppingListItemSelection(index);
	}

	handleShoppingListItemSelection(index: number): void {
		this.shoppingListService.setState({
			selectedItemIndex: index,
		});

		this.navigateToSearch(index);
		this.updateMainSearchBar(this.shoppingListItems[index]);
	}

	inputKeyup(event: any): void {
		if (!(event instanceof KeyboardEvent)) {
			return;
		}

		if (event.keyCode === ENTER && this.isInEditMode) {
			this.editInput.input.nativeElement.blur();
			this.updateItem();
		}

		if (event.keyCode === ESCAPE) {
			this.focusOnShoppingListItem(this.indexOfItemBeingEdited || 0);
			this.toggleEditMode(false);
		}
	}

	navigateToSearch(index: number): void {
		this.router.navigate([this.appSettingsService.getSetting('pageUrl').productSearch], {
			queryParams: {
				search: this.shoppingListItems[index],
				dasFilter: null,
				filters: null,
				page: 1,
				size: 24,
			},
			queryParamsHandling: 'merge',
		});
	}

	updateItem(): void {
		if (
			this.shoppingListEditForm.get('itemName')?.value !==
			this.shoppingListItems[this.indexOfItemBeingEdited || 0]
		) {
			const updatedItems = this.shoppingListItems;

			updatedItems[this.indexOfItemBeingEdited || 0] = this.shoppingListEditForm.get('itemName')?.value;

			this.shoppingListService.updateShoppingList(updatedItems);
		}

		this.focusOnShoppingListItem(this.indexOfItemBeingEdited || 0);
		this.navigateToSearch(this.indexOfItemBeingEdited || 0);
		this.toggleEditMode(false);

		this.shoppingListElem.nativeElement.scrollLeft = this.currentScrollLeft;

		this.indexOfItemBeingEdited = undefined;
	}

	updateMainSearchBar(value: string): void {
		if (this.searchbarElem) {
			this.searchbarElem.value = value;
		}
	}
}
